"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useEffect } from "react";
import { WebSocketProvider } from "./WebSocketProvider";
import type { WebSocketConfig } from "./index";
import { DEFAULT_WEBSOCKET_CONFIG } from "./index";
import { useWebSocket } from "./useWebSocket";
import { useWebSocketHealth } from "./useWebSocketHealth";

/**
 * WebSocket connection initializer with coordinated health monitoring
 *
 * EAGER INITIALIZATION PATTERN:
 * - Connects IMMEDIATELY on mount when auth token exists
 * - Delegates all connection management to useWebSocketHealth()
 * - Health monitoring detects auth token and connects during first render cycle
 * - Provides cleanup on unmount
 * 
 * This ensures immediate connectivity for authenticated users while
 * maintaining centralized connection management through health monitoring.
 */
function WebSocketInitializer() {
  const $ = _c(3);
  const {
    disconnect
  } = useWebSocket();
  useWebSocketHealth();
  let t0;
  let t1;
  if ($[0] !== disconnect) {
    t0 = () => () => {
      disconnect();
    };
    t1 = [disconnect];
    $[0] = disconnect;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  return null;
}

/**
 * Application-level WebSocket provider
 *
 * Wraps the app with WebSocket provider and initializes connection
 */
export function AppWebSocketProvider(t0) {
  const $ = _c(4);
  const {
    children,
    config: t1
  } = t0;
  const config = t1 === undefined ? DEFAULT_WEBSOCKET_CONFIG : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <WebSocketInitializer />;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== children || $[2] !== config) {
    t3 = <WebSocketProvider config={config}>{t2}{children}</WebSocketProvider>;
    $[1] = children;
    $[2] = config;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}